import { createContext, type ReactNode, useContext } from "react";
import { cn } from "../lib/cn";
import { CountBadge } from "./CountBadge";
import { Kbd } from "./Kbd";
import { Tooltip } from "./Tooltip";

const CollapsedContext = createContext(false);

export interface AppShellProps {
  sidebar: ReactNode;
  /** Optional title bar (desktop). Draggable in Tauri via data-tauri-drag-region. */
  titleBar?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** Sidebar plus content. The sidebar is Vertec Black (#101010) in both themes. */
export function AppShell({ sidebar, titleBar, children, className }: AppShellProps) {
  return (
    <div className={cn("flex h-full min-h-0 flex-col bg-canvas text-ink", className)}>
      {titleBar}
      <div className="flex min-h-0 flex-1">
        {sidebar}
        {/* Scrolls down only. Clipping across means a panel sliding in beside it (the docked
            Assistant) can never make the page scroll sideways while it animates. */}
        <main className="min-w-0 flex-1 overflow-x-clip overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}

export interface TitleBarProps {
  children?: ReactNode;
  className?: string;
}

export function TitleBar({ children, className }: TitleBarProps) {
  return (
    <div
      data-theme="dark"
      data-tauri-drag-region
      className={cn(
        "flex h-10 shrink-0 items-center gap-3 border-hairline border-b bg-sidebar px-3 text-ink",
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface SidebarProps {
  /** Icon rail at 56px, for compact windows. */
  collapsed?: boolean;
  header?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Sidebar({ collapsed = false, header, footer, children, className }: SidebarProps) {
  return (
    <CollapsedContext.Provider value={collapsed}>
      <aside
        data-theme="dark"
        className={cn(
          "flex shrink-0 flex-col gap-1 border-hairline border-r bg-sidebar py-3 text-ink",
          collapsed ? "w-14 items-center px-2" : "w-56 px-3",
          className,
        )}
      >
        {header ? <div className={cn("mb-3", collapsed ? "" : "px-1")}>{header}</div> : null}
        {/* Scrolls on a short window; the header and the footer stay put. */}
        <nav
          className={cn(
            "-mx-1 flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-1",
            collapsed && "items-center",
          )}
        >
          {children}
        </nav>
        {footer ? <div className="mt-2 flex flex-col gap-0.5">{footer}</div> : null}
      </aside>
    </CollapsedContext.Provider>
  );
}

/**
 * A group of sidebar items. With a label: a small heading over the items (`micro`, `ink-4`),
 * and the group is named for assistive tech. On the narrow rail the label gives way to a short
 * hairline between groups, so the sections still read without text.
 */
export function SidebarSection({ label, children }: { label?: string; children: ReactNode }) {
  const collapsed = useContext(CollapsedContext);
  return (
    <div
      {...(label ? { role: "group", "aria-label": label } : {})}
      className={cn(
        "flex flex-col gap-0.5",
        collapsed ? "items-center [&:first-child>hr]:hidden" : "mt-3 first:mt-0",
      )}
    >
      {collapsed ? (
        <hr aria-hidden className="my-1.5 w-6 border-0 border-hairline border-t" />
      ) : label ? (
        <p aria-hidden className="px-2.5 pb-1 font-medium text-micro text-ink-4">
          {label}
        </p>
      ) : null}
      {children}
    </div>
  );
}

export interface SidebarItemProps {
  icon: ReactNode;
  label: string;
  active?: boolean;
  count?: number;
  /** A dot instead of a count, for example a missed call. */
  attention?: boolean;
  onClick?: () => void;
  href?: string;
  /** Keyboard shortcut, shown on hover (in the tooltip when the sidebar is collapsed). */
  shortcut?: string;
  /** Unread count as an accent circle ("Report a problem"). Hidden at 0. */
  badge?: number;
  /**
   * What the collapsed sidebar's tooltip says, when the label alone is ambiguous next to its
   * shortcut ("Assistant page" for an item whose label is "Assistant"). Defaults to the label.
   */
  tooltip?: string;
}

export function SidebarItem({
  icon,
  label,
  active,
  count,
  badge,
  attention,
  onClick,
  href,
  shortcut,
  tooltip,
}: SidebarItemProps) {
  const collapsed = useContext(CollapsedContext);
  const name = badge ? `${label}, ${badge} unread` : label;
  const cls = cn(
    "press group/item relative flex h-8 items-center gap-2.5 rounded-md font-medium text-ui outline-none",
    "focus-visible:shadow-[0_0_0_2px_var(--vd-focus)] [&_svg]:size-4 [&_svg]:shrink-0",
    collapsed ? "w-10 justify-center" : "w-full px-2.5",
    active ? "bg-fill-3 text-ink" : "text-ink-2 hover:bg-fill-2 hover:text-ink",
  );
  const showKeys = Boolean(!collapsed && shortcut && count === undefined && !badge);
  const inner = (
    <>
      <span className={cn("flex", active ? "text-ink" : "text-ink-3")}>{icon}</span>
      {collapsed ? null : showKeys ? (
        // The keys share a wrapping row with the label: when both don't fit, the keys wrap to
        // a second line the 32px item clips, so the label is never cut for them.
        <span className="flex h-8 min-w-0 flex-1 flex-wrap content-start items-center gap-x-2 overflow-hidden">
          <span className="max-w-full truncate text-left leading-8">{label}</span>
          <Kbd
            keys={shortcut as string}
            size="sm"
            aria-hidden
            className="ml-auto hidden group-hover/item:inline-flex group-focus-visible/item:inline-flex"
          />
        </span>
      ) : (
        <span className="flex-1 truncate text-left">{label}</span>
      )}
      {!collapsed && count !== undefined ? (
        <span className="text-micro text-ink-3 tnum">{count}</span>
      ) : null}
      {badge ? (
        <CountBadge
          count={badge}
          className={cn(collapsed && "absolute top-0 right-0 shadow-[0_0_0_2px_var(--vd-sidebar)]")}
        />
      ) : null}
      {attention ? (
        <span
          className={cn(
            "size-1.5 rounded-pill bg-danger",
            collapsed && "absolute top-1.5 right-1.5",
          )}
        />
      ) : null}
    </>
  );
  const el = href ? (
    <a
      href={href}
      aria-current={active ? "page" : undefined}
      className={cls}
      aria-label={collapsed || badge ? name : undefined}
    >
      {inner}
    </a>
  ) : (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      aria-label={collapsed || badge ? name : undefined}
      className={cls}
    >
      {inner}
    </button>
  );
  return collapsed ? (
    <Tooltip content={tooltip ?? label} side="right" {...(shortcut ? { shortcut } : {})}>
      {el}
    </Tooltip>
  ) : (
    el
  );
}

export interface PageHeaderProps {
  title: string;
  /** One short line under the title, for counts or scope. Optional. */
  meta?: ReactNode;
  actions?: ReactNode;
  /** The breadcrumb or back link over the title (`Breadcrumbs`), on a nested page. */
  crumbs?: ReactNode;
  className?: string;
}

/** Page title row for the console and desktop settings. */
export function PageHeader({ title, meta, actions, crumbs, className }: PageHeaderProps) {
  return (
    <div className={cn("flex items-end justify-between gap-4 pb-5", className)}>
      <div className="flex min-w-0 flex-col gap-1">
        {crumbs ? <div className="-mt-1 mb-1 empty:hidden">{crumbs}</div> : null}
        <h1 className="truncate font-display font-medium text-display">{title}</h1>
        {meta ? <p className="text-caption text-ink-3">{meta}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}
